/**
 * Centralized authentication session management.
 *
 * Replaces scattered auth patterns with a single abstraction:
 * - Token persistence (localStorage)
 * - Authenticated GraphQL client creation
 * - Viewer fetching and Redux state sync
 * - Rate display cookie management
 */
import { setUserContext } from '@/actions/ui';
import config from '@/config';
import { GraphQLClient, bearerAuth } from '@/core/api';
import { getPermissionService } from '@/core/permissions';
import { getDefaultUserContext } from '@/core/user';
import { Viewer, fetchViewer } from '@/core/viewer';
import store, { getThunkExtraArg } from '@/store';
import { setRuntimeVariable } from '@/store/runtime';
import { clearCache, getCache, rateDisplay, setCache } from '@/utils';

import { setViewer } from '../store/viewerSlice';
import { AUTH_TOKEN_CACHE_KEY as TOKEN_KEY } from './authTokenKey';

interface TokenCache {
  token: string;
}

export const authSession = {
  /**
   * Store auth token in localStorage
   */
  setToken(token: string): void {
    setCache(TOKEN_KEY, { token } as TokenCache);
  },

  /**
   * Retrieve auth token from localStorage
   */
  getToken(): string | null {
    const cached = getCache(TOKEN_KEY) as TokenCache | undefined;
    return cached?.token ?? null;
  },

  /**
   * Clear auth token from localStorage and runtime Redux state.
   *
   * ENG-5251 (PR #2688 review): also dispatches `setRuntimeVariable('token',
   * undefined)` so the ApiWebSocket token-getter (boot-time lazy accessor at
   * `index.tsx:252`) sees the logout symmetrically with mid-session signup.
   * Previously logout left `runtime.token` stale until the next page reload.
   */
  clearToken(): void {
    clearCache(TOKEN_KEY);
    store.dispatch(setRuntimeVariable('token', undefined));
    // ENG-5251 (PR #2688 review SEC-1): tear down the WebSocket
    // subscription registry on identity teardown so the next authenticated
    // socket doesn't replay the prior identity's subscribe frames. Covers
    // both logout and loginAs (loginAs → logout() → clearToken()). Guarded
    // because thunkExtraArg.apiWebSocket is unset until app boot.
    getThunkExtraArg().apiWebSocket?.reset();
  },

  /**
   * Create an authenticated GraphQL client.
   * Uses provided token or falls back to cached token.
   */
  createClient(token?: string): GraphQLClient {
    const authToken = token ?? this.getToken();
    if (!authToken) {
      throw new Error('No auth token available');
    }
    return new GraphQLClient(config.apiUrl, bearerAuth(authToken));
  },

  /**
   * Full authentication flow:
   * 1. Store token in cache
   * 2. Create authenticated client
   * 3. Fetch viewer data
   * 4. Sync Redux state
   * 5. Set rate display cookie
   *
   * @returns The authenticated viewer
   */
  async authenticate(token: string): Promise<Viewer> {
    const client = new GraphQLClient(config.apiUrl, bearerAuth(token));
    const viewer = await fetchViewer(client.client); // Validate first

    this.setToken(token); // Only store if successful
    // ENG-5251 (PR #2688 review): dispatch runtime.token so the
    // ApiWebSocket lazy token-getter (index.tsx:252) sees the new auth.
    // Without this, mid-session signup would never propagate to the
    // WebSocket — currentToken === lastToken (both stale boot value), the
    // token-swap branch is skipped, and the socket stays anonymous.
    store.dispatch(setRuntimeVariable('token', token));
    store.dispatch(setViewer(viewer));

    // Rebind the boot-time PermissionService instance to the new viewer
    // ID. The router middleware context captured the boot-time service
    // (constructed with viewer.id="" for a logged-out self-service signup),
    // so every route loader's permission.allowedBatch call would otherwise
    // query the API with an empty userId — yielding null results and
    // throwing inside buildTeamNav (TypeError on perms[0].allowed →
    // "Something went wrong" on first Settings visit). Rebinding the same
    // instance updates every captured reference at once.
    getPermissionService()?.rebindUserId(viewer.id);

    // Re-derive userContextOptions from the freshly-fetched viewer so any
    // newly-acquired team (e.g., post-signup, post-team-join) shows up in
    // the Settings nav and elsewhere without forcing a page reload. The
    // app-boot path in index.tsx runs this once at start; for mid-session
    // authentication we run it here. Preserves the user's chosen
    // userContext via getDefaultUserContext's currentContext arg.
    const { userContext } = store.getState().ui;
    await store.dispatch(setUserContext(getDefaultUserContext(viewer, userContext)));

    // Set rate display cookie (persists after logout)
    rateDisplay.setFromBillingAccount(viewer.groups?.[0]?.billing_account);

    return viewer;
  },

  /**
   * Check if user is authenticated (has token in cache)
   */
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  },
};
